import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Save, Search, UserPlus } from 'lucide-react'
import { Modal } from '../../../componentesCompartilhados/ui/Modal'
import { FormField, FileUpload } from '../../../componentesCompartilhados/form'
import { ClockifyUsuario, UsuarioFormData } from '../tipos/usuario'
import { useCreateUsuario, useUpdateUsuario, usePessoasAtivas, usePapeisDisponiveis } from '../hooks/useUsuario'
import { usuarioService } from '../services/usuario'
import { zRequiredString, zRequiredEmail } from '../../../utils/validacao'
import { useUsuario } from '../../../contexts/UsuarioContext'

interface FormularioUsuarioProps {
  isOpen: boolean
  onClose: () => void
  usuario?: UsuarioFormData | null
  isEditing?: boolean
}

const baseSchema = z.object({
  id: z.string().optional(),
  nome: zRequiredString,
  email: zRequiredEmail,
  senha: z.string().optional(),
  confirmarSenha: z.string().optional(),
  ativo: z.boolean().default(true),
  pessoaId: zRequiredString,
  papelCodigo: z.string().min(1, 'Selecione um papel'),
  userIdClockify: z.string().optional(),
})

const createSchema = baseSchema
  .extend({
    senha: z.string().min(6, 'Deve ter pelo menos 6 caracteres.'),
    confirmarSenha: z.string().min(6, 'Deve ter pelo menos 6 caracteres.'),
  })
  .superRefine((data, ctx) => {
    if (data.senha !== data.confirmarSenha) {
      ctx.addIssue({
        path: ['confirmarSenha'],
        code: z.ZodIssueCode.custom,
        message: 'As senhas devem ser iguais.',
      })
    }
  })

const updateSchema = baseSchema.superRefine((data, ctx) => {
  const senhaInformada = data.senha && data.senha.trim().length > 0
  const confirmacaoInformada = data.confirmarSenha && data.confirmarSenha.trim().length > 0

  if (senhaInformada || confirmacaoInformada) {
    if ((data.senha?.length || 0) < 6) {
      ctx.addIssue({
        path: ['senha'],
        code: z.ZodIssueCode.custom,
        message: 'Deve ter pelo menos 6 caracteres.',
      })
    }

    if (data.senha !== data.confirmarSenha) {
      ctx.addIssue({
        path: ['confirmarSenha'],
        code: z.ZodIssueCode.custom,
        message: 'As senhas devem ser iguais.',
      })
    }
  }
})

export function FormularioUsuario({ isOpen, onClose, usuario, isEditing = false }: FormularioUsuarioProps) {
  const schema = useMemo(() => (isEditing ? updateSchema : createSchema), [isEditing])
  const { data: papeis = [], isLoading: isLoadingPapeis } = usePapeisDisponiveis()
  const { data: pessoasAtivas = [], isLoading: isLoadingPessoas } = usePessoasAtivas()
  const { usuario: usuarioLogado } = useUsuario()
  const isAdministrador = usuarioLogado?.papeis?.includes('1') ?? false
  const [foto, setFoto] = useState<File | null>(null)
  const [modalClockifyAberto, setModalClockifyAberto] = useState(false)
  const [clockifyUsuarios, setClockifyUsuarios] = useState<ClockifyUsuario[]>([])
  const [clockifyCarregando, setClockifyCarregando] = useState(false)
  const [clockifyErro, setClockifyErro] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<UsuarioFormData>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    defaultValues: {
      id: '',
      nome: '',
      email: '',
      senha: '',
      confirmarSenha: '',
      ativo: true,
      pessoaId: '',
      papelCodigo: '',
      userIdClockify: '',
    },
  })

  const createMutation = useCreateUsuario()
  const updateMutation = useUpdateUsuario()

  const abrirBuscaClockify = async () => {
    const email = getValues('email')?.trim()
    setClockifyErro(null)
    setClockifyUsuarios([])
    setModalClockifyAberto(true)

    if (!email)
    {
      setClockifyErro('Informe o e-mail do usuário para buscar no Clockify')
      return
    }

    setClockifyCarregando(true)
    try {
      const usuarios = await usuarioService.buscarUsuariosClockify(email)
      setClockifyUsuarios(usuarios)
    } catch (error: any) {
      setClockifyErro(error?.message || 'Erro ao buscar usuários no Clockify')
    } finally {
      setClockifyCarregando(false)
    }
  }

  const vincularUsuarioClockify = (usuarioClockify: ClockifyUsuario) => {
    if (!usuarioClockify.id)
      return

    setValue('userIdClockify', usuarioClockify.id, { shouldDirty: true, shouldValidate: true })
    setModalClockifyAberto(false)
  }

  useEffect(() => {
    if (usuario) {
      reset({
        id: usuario.id || '',
        nome: usuario.nome || '',
        email: usuario.email || '',
        senha: '',
        confirmarSenha: '',
        ativo: usuario.ativo ?? true,
        pessoaId: usuario.pessoaId || '',
        papelCodigo: usuario.papelCodigo !== undefined ? String(usuario.papelCodigo) : '',
        userIdClockify: usuario.userIdClockify || '',
      })
      setFoto(null)
    } else {
      reset({
        id: '',
        nome: '',
        email: '',
        senha: '',
        confirmarSenha: '',
        ativo: true,
        pessoaId: '',
        papelCodigo: '',
        userIdClockify: '',
      })
      setFoto(null)
    }
  }, [usuario, reset, isOpen])

  const handleFotoSelect = (file: File | null) => {
    setFoto(file)
  }

  const handleFormSubmit = async (formData: UsuarioFormData) => {
    try {
      const payload: UsuarioFormData = {
        ...formData,
        senha: formData.senha && formData.senha.trim().length > 0 ? formData.senha : undefined,
        confirmarSenha: undefined,
        foto: foto || undefined,
      }

      if (isEditing && formData.id) {
        await updateMutation.mutateAsync({ id: formData.id, data: payload })
        
        // Se há foto, fazer upload separado
        if (foto && formData.id) {
          await usuarioService.uploadFoto(formData.id, foto)
        }
      } else {
        const created = await createMutation.mutateAsync(payload)
        
        // Se há foto, fazer upload após criação
        if (foto && created?.id) {
          await usuarioService.uploadFoto(created.id, foto)
        }
      }

      onClose()
      reset()
      setFoto(null)
    } catch (error) {
      // Erros já são tratados pelo toast nos hooks, apenas mantemos o modal aberto
    }
  }

  const footer = (
    <div className="flex justify-end space-x-3 px-6 py-4">
      <button
        onClick={() => {
          onClose()
          reset()
        }}
        className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 font-medium"
        type="button"
      >
        Cancelar
      </button>
      <button
        type="submit"
        form="usuario-form"
        disabled={isSubmitting || createMutation.isLoading || updateMutation.isLoading}
        className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Save className="w-4 h-4 mr-2 inline" />
        {isEditing ? 'Atualizar' : 'Salvar'}
      </button>
    </div>
  )

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          onClose()
          reset()
        }}
        title={isEditing ? 'Editar usuário' : 'Novo usuário'}
        subtitle={isEditing ? 'Atualize as informações do usuário selecionado' : 'Preencha os dados para criar um novo usuário'}
        icon={
          <div className="w-10 h-10 bg-brand-100 text-brand-600 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
            <UserPlus className="w-5 h-5" />
          </div>
        }
        size="2xl"
        footer={footer}
      >
        <form className="space-y-6" onSubmit={handleSubmit(handleFormSubmit)} id="usuario-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              label="Nome completo"
              error={errors.nome}
              labelClassName="block text-sm font-medium text-gray-700 mb-1"
            >
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                placeholder="Digite o nome do usuário"
                {...register('nome')}
              />
            </FormField>

          <FormField
            label="E-mail"
            error={errors.email}
            labelClassName="block text-sm font-medium text-gray-700 mb-1"
          >
            <input
              type="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
              placeholder="usuario@empresa.com"
              {...register('email')}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            label={
              <>
                Senha {isEditing ? '(deixe em branco para manter a atual)' : ''}
              </>
            }
            error={errors.senha}
            labelClassName="block text-sm font-medium text-gray-700 mb-1"
          >
            <input
              type="password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
              placeholder="********"
              autoComplete="new-password"
              {...register('senha')}
            />
          </FormField>

          <FormField
            label="Confirmar senha"
            error={errors.confirmarSenha}
            labelClassName="block text-sm font-medium text-gray-700 mb-1"
          >
            <input
              type="password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
              placeholder="********"
              autoComplete="new-password"
              {...register('confirmarSenha')}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            label="Pessoa vinculada"
            error={errors.pessoaId}
            labelClassName="block text-sm font-medium text-gray-700 mb-1"
          >
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
              {...register('pessoaId')}
              disabled={isLoadingPessoas}
            >
              <option value="">Selecione uma pessoa</option>
              {pessoasAtivas.map((pessoa) => (
                <option key={pessoa.idPessoa} value={pessoa.idPessoa}>
                  {pessoa.nome} — {pessoa.email}
                </option>
              ))}
            </select>
          </FormField>

          <div className="flex items-center gap-3 mt-6 md:mt-8">
            <input
              type="checkbox"
              id="usuario-ativo"
              className="h-4 w-4 text-brand-500 focus:ring-brand-400 border-gray-300 rounded"
              {...register('ativo')}
            />
            <label htmlFor="usuario-ativo" className="text-sm text-gray-700">
              Usuário ativo
            </label>
          </div>
        </div>

        {/* Foto */}
        <FormField label="Foto" error={undefined}>
          <FileUpload
            accept="image/jpeg,image/jpg,image/png"
            onFileSelect={handleFotoSelect}
            selectedFile={foto}
            label=""
            showPreview={true}
          />
        </FormField>

        {/* UserIdClockify - Apenas para admin */}
        {isAdministrador && (
          <FormField
            label="Usuário Clockify"
            error={errors.userIdClockify}
            labelClassName="block text-sm font-medium text-gray-700 mb-1"
          >
            <div className="flex gap-2">
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                placeholder="ID do usuário no Clockify (opcional)"
                readOnly
                {...register('userIdClockify')}
              />
              <button
                type="button"
                onClick={abrirBuscaClockify}
                className="flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-all duration-200"
                title="Buscar usuário no Clockify"
              >
                <Search className="w-4 h-4" />
              </button>
            </div>
          </FormField>
        )}

          <FormField
            label="Papel"
            error={errors.papelCodigo}
            labelClassName="block text-sm font-medium text-gray-700 mb-2"
          >
            {isLoadingPapeis ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center text-sm text-gray-500">
                Carregando papéis...
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                {papeis.map((papel) => (
                  <label key={papel.codigo} className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      value={papel.codigo}
                      {...register('papelCodigo')}
                      className="mt-1 h-4 w-4 text-brand-500 focus:ring-brand-400 border-gray-300"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">{papel.nome}</span>
                      {papel.descricao && <p className="text-xs text-gray-500">{papel.descricao}</p>}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </FormField>
        </form>
      </Modal>

      <Modal
        isOpen={modalClockifyAberto}
        onClose={() => setModalClockifyAberto(false)}
        title="Usuários do Clockify"
        subtitle="Selecione um usuário para vincular"
        icon={
          <div className="w-10 h-10 bg-brand-100 text-brand-600 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
            <Search className="w-5 h-5" />
          </div>
        }
        size="4xl"
      >
        {clockifyCarregando && (
          <div className="text-sm text-gray-600">Carregando usuários...</div>
        )}

        {!clockifyCarregando && clockifyErro && (
          <div className="text-sm text-red-600">{clockifyErro}</div>
        )}

        {!clockifyCarregando && !clockifyErro && clockifyUsuarios.length === 0 && (
          <div className="text-sm text-gray-600">Nenhum usuário encontrado.</div>
        )}

        {!clockifyCarregando && !clockifyErro && clockifyUsuarios.length > 0 && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 text-gray-600 font-medium">Nome</th>
                  <th className="text-left px-4 py-2 text-gray-600 font-medium">E-mail</th>
                  <th className="text-left px-4 py-2 text-gray-600 font-medium">ID</th>
                  <th className="text-right px-4 py-2 text-gray-600 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {clockifyUsuarios.map((usuarioClockify) => (
                  <tr key={usuarioClockify.id} className="border-t border-gray-100">
                    <td className="px-4 py-2 text-gray-800">{usuarioClockify.nome}</td>
                    <td className="px-4 py-2 text-gray-800">{usuarioClockify.email || '-'}</td>
                    <td className="px-4 py-2 text-gray-500">{usuarioClockify.id}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => vincularUsuarioClockify(usuarioClockify)}
                        className="px-3 py-1.5 text-sm text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200"
                      >
                        Vincular
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </>
  )
}

